import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DocumentsTreeHeader from 'app/shared/components/document/DocumentsTreeHeader';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { openDialogAction } from 'store/global/actions';
import { selectIsLoanCanceled } from 'store/loanApplications/selectors';
import { getDocumentBundlesAction } from 'store/system/actions';
import { selectDocumentBundlesValues } from 'store/system/selectors';

import GenerateBundleModal from '../GenerateBundleModal';

import { getCheckboxesHeaderConfig, getHeaderIconButtonsConfig } from './Header.config';

const defaultToolbarOptions = {};

const Header = ({
  loanParams,
  onChangeEmptyTreeItemVisibility,
  isEmptyTreeItemVisible,
  isDeletedDocumentsVisible,
  onChangeDeletedDocumentsVisibility,
  isRejectedOnlyVisible,
  onChangeRejectedOnlyVisibility,
  onCollapse = _.noop,
  onCollapsePanelLeft = null,
  isAllExpanded = true,
  toolbarOptions = defaultToolbarOptions,
  totalDocumentCount = 0,
}) => {
  const dispatch = useDispatch();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const documentBundleTypes = useSelector(selectDocumentBundlesValues);
  const isGenerateBundleAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.DOCUMENTS_GENERATE_BUNDLE),
  );
  const isLoanCanceled = useSelector(selectIsLoanCanceled);

  useEffectSafeStrictMode(() => {
    if (!isDataLoaded) {
      dispatch(getDocumentBundlesAction({}))
        .then(() => setIsDataLoaded(true))
        .catch(Error);
    }
  }, [isDataLoaded, dispatch]);

  const onGenerateBundleClick = useCallback(() => {
    const abortController = new AbortController();

    dispatch(
      openDialogAction({
        header: 'Generate bundle',
        body: dialogProps => (
          <GenerateBundleModal
            abortController={abortController}
            documentBundleTypes={documentBundleTypes}
            loanParams={loanParams}
            {...dialogProps}
          />
        ),
        afterCloseCallback: () => abortController.abort(),
      }),
    );
  }, [dispatch, documentBundleTypes, loanParams]);

  const iconButtonsConfig = useMemo(
    () =>
      getHeaderIconButtonsConfig({
        isGenerateBundleIconDisabled: !documentBundleTypes?.length,
        onGenerateBundleClick,
        isGenerateBundleAllowed: isGenerateBundleAllowed && !isLoanCanceled,
      }),
    [documentBundleTypes?.length, isLoanCanceled, isGenerateBundleAllowed, onGenerateBundleClick],
  );

  const checkboxesConfig = useMemo(
    () =>
      getCheckboxesHeaderConfig({
        isEmptyTreeItemVisible,
        isDeletedDocumentsVisible,
        isRejectedOnlyVisible,
        onChangeEmptyTreeItemVisibility,
        onChangeDeletedDocumentsVisibility,
        onChangeRejectedOnlyVisibility,
      }),
    [
      isEmptyTreeItemVisible,
      isDeletedDocumentsVisible,
      isRejectedOnlyVisible,
      onChangeEmptyTreeItemVisibility,
      onChangeDeletedDocumentsVisibility,
      onChangeRejectedOnlyVisibility,
    ],
  );

  return (
    <DocumentsTreeHeader
      checkboxesConfig={checkboxesConfig}
      iconButtonsConfig={iconButtonsConfig}
      isAllExpanded={isAllExpanded}
      toolbarOptions={toolbarOptions}
      totalDocumentCount={totalDocumentCount}
      onCollapse={onCollapse}
      onCollapsePanelLeft={onCollapsePanelLeft}
    />
  );
};

Header.propTypes = {
  isDeletedDocumentsVisible: PropTypes.bool.isRequired,
  isEmptyTreeItemVisible: PropTypes.bool.isRequired,
  loanParams: PropTypes.object.isRequired,
  onChangeDeletedDocumentsVisibility: PropTypes.func.isRequired,
  onChangeEmptyTreeItemVisibility: PropTypes.func.isRequired,
  isAllExpanded: PropTypes.bool,
  isRejectedOnlyVisible: PropTypes.bool,
  toolbarOptions: PropTypes.object,
  totalDocumentCount: PropTypes.number,
  onChangeRejectedOnlyVisibility: PropTypes.func,
  onCollapse: PropTypes.func,
  onCollapsePanelLeft: PropTypes.func,
};

export default Header;
